import { authPageMessages } from "./auth-pages";
import { dashboardOrgPageMessages } from "./dashboard-org-pages";
import { dashboardPageMessages } from "./dashboard-pages";
import { mergeUiMessages } from "./merge";
import { publicDirectoryPageMessages } from "./public-directory-pages";
import { publicModelPageMessages } from "./public-model-pages";
import { publicPageMessages } from "./public-pages";

import type { Locale, TranslationTree } from "@llmgateway/i18n";

const baseUiMessages = {
	en: {
		landing: {
			nav: {
				ai: {
					agents: {
						title: "Agents",
						description: "Pre-built AI agents with tool calling capabilities.",
					},
					agentSkills: {
						title: "Agent Skills",
						description: "Skills for Claude Code and other AI agents.",
					},
					aiSdk: {
						description: "Use doteb with Vercel's AI SDK.",
					},
					guides: {
						title: "Guides",
						description: "Integration and usage guides for every framework.",
					},
					mcp: {
						description: "Connect AI assistants to 200+ LLMs via MCP protocol.",
					},
					templates: {
						title: "Templates",
						description: "Production-ready templates for AI applications.",
					},
				},
				closeMenu: "Close Menu",
				dashboard: "Dashboard",
				docs: "Docs",
				getStarted: "Get Started",
				home: "home",
				login: "Log In",
				models: "Models",
				openMenu: "Open Menu",
				pricing: "Pricing",
				productsLabel: "Products",
				resourcesLabel: "Resources",
				products: {
					aiGateway: {
						title: "AI Gateway",
						description:
							"Route requests to 200+ LLMs through a single, unified API endpoint.",
					},
					chatPlayground: {
						title: "Chat Playground",
						description:
							"Test prompts and compare model responses side by side, instantly.",
					},
					devPass: {
						description:
							"Fixed-price monthly plans for Claude Code, Cursor, and every coding tool.",
					},
					observability: {
						title: "Observability",
						description:
							"Monitor usage, costs, and latency with real-time analytics dashboards.",
					},
				},
				resources: {
					apps: {
						title: "Apps",
						description: "Browse apps and tools that work with doteb.",
					},
					blog: {
						title: "Blog",
						description:
							"Product updates, tutorials, benchmarks, and announcements.",
					},
					changelog: {
						title: "Changelog",
						description: "What's new in doteb across releases.",
					},
					compare: {
						title: "Compare",
						description: "Compare models side by side.",
					},
					enterprise: {
						title: "Enterprise",
						description:
							"Custom billing, extended retention, and priority support for teams.",
					},
					guardrails: {
						title: "Guardrails",
						description:
							"Protect your AI with content moderation and safety filters.",
					},
					integrations: {
						title: "Integrations",
						description:
							"Connect seamlessly with popular frameworks, SDKs, and tools.",
					},
					models: {
						title: "Models",
						description: "Browse all available LLM models and capabilities.",
					},
					modelTimeline: {
						title: "Model Timeline",
						description: "Track the release history of all models.",
					},
					providers: {
						title: "Providers",
						description: "Connect and manage your provider API keys.",
					},
					referralProgram: {
						title: "Referral Program",
						description: "Earn 1% of LLM spending.",
					},
					reliability: {
						title: "Reliability",
						description:
							"Automatic failover and 99.9999% effective uptime across providers.",
					},
					tokenCostCalculator: {
						title: "Token Cost Calculator",
						description:
							"Calculate your LLM token costs and savings instantly.",
					},
				},
			},
			hero: {
				announcement: "doteb Is Now SOC 2 Type II Certified",
				dashboardAlt: "doteb dashboard showing analytics and API usage",
				docsCta: "View documentation",
				primaryCta: "Get My API Key",
				subtitle:
					"Stop juggling API keys and provider dashboards. Route requests across 400+ models, track costs in real-time, and switch providers without changing your code.",
				switching: "Switching from another provider?",
				title: "doteb — One API for DeepSeek, Qwen, Kimi, and 40+ providers",
				trust: {
					freeTier: "Free tier included",
					noCard: "No credit card required",
					setup: "Setup in 30 seconds",
				},
				viewAll: "View all",
				viewAllProviders: "View All Providers",
			},
			features: {
				eyebrow: "Platform Capabilities",
				learnMore: "Learn more",
				titleLine1: "Everything you need to",
				titleLine2: "ship with confidence",
				items: {
					"unified-api-interface": {
						title: "Unified API Interface",
						description:
							"Keep your existing OpenAI SDK code - just change the base URL and you're live.",
					},
					"multi-provider-support": {
						title: "Multi-provider Support",
						description:
							"Access 25+ providers through one integration - no vendor lock-in, switch models instantly.",
					},
					"performance-monitoring": {
						title: "Performance Monitoring",
						description:
							"Compare latency, cost, and quality across models to pick the best fit for each use case.",
					},
					"secure-key-management": {
						title: "Secure Key Management",
						description:
							"One dashboard for all your provider keys - no more scattered credentials or exposed secrets.",
					},
					"self-hosted-or-cloud": {
						title: "Self-hosted or Cloud",
						description:
							"Run on your own infrastructure for full control, or let us handle it - your choice.",
					},
					"cost-aware-analytics": {
						title: "Cost-aware analytics",
						description:
							"See requests, tokens, total spend, and average cost per 1K tokens across 7 or 30 days.",
					},
					"per-model-provider-breakdown": {
						title: "Per-model/provider breakdown",
						description:
							"Break down usage and spend by provider and model so you can quickly spot expensive outliers.",
					},
					"errors-reliability-monitoring": {
						title: "Errors & reliability monitoring",
						description:
							"Monitor error rate, cache hit rate, and reliability trends directly from the dashboard.",
					},
					"project-level-usage-explorer": {
						title: "Project-level usage explorer",
						description:
							"Drill into each project's requests, models, errors, cache, and costs with dedicated charts and tables.",
					},
					"audit-logs": {
						title: "Enterprise Audit Logs",
						description:
							"Track who did what, when, and maintain compliance with comprehensive audit trails.",
					},
					guardrails: {
						title: "LLM Guardrails",
						description:
							"Prevent prompt injection, detect PII, and block malicious requests with intelligent guardrails.",
					},
				},
			},
			graph: {
				description:
					"Your app sends one request. We route it to DeepSeek, Qwen, Kimi, or any of 40+ providers - automatically picking the best path.",
				eyebrow: "How It Works",
				requestModel: "Request Model",
				stats: {
					models: "Models",
					providers: "Providers",
					tokens: "Tokens routed",
				},
				title: "One request. Any model.",
				tooltipLink: "Get in touch",
				tooltipPrefix: "Could be your model?",
				viewAllModels: "View all models",
			},
			codeExample: {
				bullets: {
					baseUrl: "Change one line - your base URL",
					sdks: "Works with OpenAI, Anthropic, and Vercel AI SDKs",
					tracking: "Every request tracked with cost, latency, and token usage",
				},
				copiedButton: "Copied",
				copiedDescription:
					"{language} code snippet has been copied to your clipboard.",
				copiedTitle: "Copied to clipboard",
				copyButton: "Copy",
				copyFailedDescription: "Could not copy to clipboard. Please try again.",
				copyFailedTitle: "Copy failed",
				description:
					"Already using OpenAI's SDK? Change one line - your base URL - and you're done. Works with any language or framework.",
				eyebrow: "Integration",
				titleLine1: "Drop-in compatible.",
				titleLine2: "Zero learning curve.",
			},
			testimonials: {
				eyebrow: "Community",
				title: "Trusted by developers worldwide",
			},
			faq: {
				contactLink: "Contact us",
				contactPrefix: "Can't find an answer?",
				description:
					"Everything you need to know about pricing, models, and getting started.",
				eyebrow: "FAQ",
				title: "Common questions",
				items: {
					openRouter: {
						answer:
							"Unlike OpenRouter, doteb offers full self-hosting, deeper real-time analytics, Bring Your Own Keys for free, and flexible enterprise add-ons.",
						intro: "Unlike OpenRouter, we offer:",
						point1:
							"Full self-hosting with a deployment package for your infrastructure.",
						point2:
							"Deeper, real-time cost and latency analytics for every request.",
						point3:
							"Bring Your Own Keys - use your own provider API keys for free.",
						point4:
							"Flexible enterprise add-ons including dedicated shards and custom SLAs.",
						question: "What makes doteb different from OpenRouter?",
						schemaAnswer:
							"Unlike OpenRouter, doteb offers full self-hosting, deeper real-time cost and latency analytics, Bring Your Own Keys for free, and flexible enterprise add-ons.",
					},
					models: {
						answerPrefix:
							"We support 400+ models across 40+ providers - including GPT-4o, Claude, Gemini, Llama, Mistral, and more. Check the",
						answerSuffix:
							"for the full list. We add new releases within 48 hours of launch.",
						modelsPage: "models page",
						question: "What models do you support?",
						schemaAnswer:
							"We support 400+ models across 40+ providers including GPT-4o, Claude, Gemini, Llama, Mistral, and more. We add new releases within 48 hours of launch.",
					},
					uptime: {
						answer:
							"Our public status page posts real-time metrics. Enterprise instances come with a 99.9% uptime SLA; self-host installations depend on your infrastructure.",
						question: "What is your uptime guarantee?",
						schemaAnswer:
							"Our public status page posts real-time metrics. Enterprise instances come with a 99.9% uptime SLA; self-host installations depend on your infrastructure.",
					},
					pricing: {
						byok: "Bring Your Own Keys - free: Use your own LLM provider API keys and pay providers directly. Usage tracking and analytics are included.",
						credits:
							"Credits - 5% fee: Pay-as-you-go credits to use any model with a flat 5% platform fee on purchases.",
						enterprise:
							"Enterprise: Custom SLA, dedicated infrastructure, bring-your-own cloud capacity, and volume discounts.",
						intro: "Our pricing is simple and transparent:",
						question: "How much does it cost?",
						schemaAnswer:
							"Credits are pay-as-you-go with a flat 5% platform fee. BYOK lets you use your own provider API keys for free. Enterprise plans include custom SLA and dedicated infrastructure. Self-hosting is available for dedicated deployments.",
						selfHost:
							"Self-host: Deploy the gateway on your own infrastructure with a supported deployment package.",
					},
				},
			},
			enterprise: {
				description:
					"When your LLM infrastructure becomes mission-critical, you need dedicated support, compliance controls, and infrastructure that matches your ambitions.",
				eyebrow: "Enterprise",
				explore: "Explore Enterprise",
				items: {
					"Enterprise SSO": {
						title: "Enterprise SSO",
						description:
							"SAML & OIDC single sign-on with role-based access control",
					},
					"Self-hosted or Managed": {
						title: "Self-hosted or Managed",
						description:
							"Deploy on your infrastructure or let us handle it with 99.9% SLA",
					},
					"Volume Pricing": {
						title: "Volume Pricing",
						description:
							"Custom rate limits and pricing that scales with your usage",
					},
					"White-label Ready": {
						title: "White-label Ready",
						description:
							"White-label gateway and chat playground with your own branding",
					},
				},
				talkToSales: "Talk to Sales",
				titleLine1: "Built for teams that",
				titleLine2: "ship at scale",
				trust: {
					onboarding: "On-boarding assistance",
					slas: "Custom SLAs",
					soc2: "SOC 2 in-progress",
					support: "Priority support",
				},
			},
			cta: {
				createAccount: "Create Free Account",
				description:
					"Join thousands of developers processing 100B+ tokens through doteb. Free tier included, no credit card required.",
				selfHost: "Self-host doteb",
				titleLine1: "Start routing requests",
				titleLine2: "in 30 seconds",
			},
			newsletter: {
				description:
					"Join developers who get weekly insights on LLM routing, new model launches, and cost optimization - straight to their inbox.",
				error: "Something went wrong. Please try again.",
				eyebrow: "Newsletter",
				noSpam: "No spam. Unsubscribe anytime.",
				perks: {
					beta: "Early access to beta features",
					models: "New models & providers as they drop",
					tips: "Tips to cut latency & costs",
				},
				subscribe: "Subscribe - it's free",
				subscribing: "Subscribing...",
				successDescription:
					"Check your inbox - we'll send you the good stuff, no filler.",
				successTitle: "You're in!",
				title: "Stay ahead of the curve",
			},
			pricingPlans: {
				badge: "Pricing",
				contactSales: "Contact Sales",
				custom: "Custom",
				description:
					"All features included in our free plan. No hidden fees or surprises.",
				footerContact: "Contact our sales team",
				footerLine1:
					"All plans include access to our API, documentation, and community support.",
				footerLine2: "Need a custom solution?",
				forever: "forever",
				free: "Free",
				getStarted: "Get Started",
				goDashboard: "Go to Dashboard",
				plans: {
					selfHost: {
						description: "Host on your own infrastructure",
						features: {
							community: "Community support",
							control: "Full control over your data",
							freeForever: "100% free forever",
							infrastructure: "Host on your infrastructure",
							noLimits: "No usage limits",
							updates: "Regular updates",
						},
						name: "Self-Host",
					},
					free: {
						description: "Full-featured plan for everyone",
						features: {
							analytics: "Advanced Analytics",
							byok: "Bring Your Own Keys (free)",
							credits: "Pay with credits (5% fee)",
							models: "Access to ALL models",
							retention: "30-day data retention",
							routing: "Auto-routing & Vendor Selection",
							support: "Email support",
							team: "Team Management",
						},
						name: "Free",
					},
					enterprise: {
						description: "For large organizations with custom needs",
						features: {
							discounts: "Volume discounts",
							everything: "Everything in Free",
							onboarding: "On-boarding assistance",
							priority: "Prioritized feature requests",
							retention: "Unlimited data retention",
							seats: "Unlimited seats",
							sso: "Single Sign-On (SSO)",
							support: "24/7 premium support",
							whiteLabel: "Chat-App (incl. whitelabel)",
						},
						name: "Enterprise",
					},
				},
				recommended: "Recommended",
				title: "Start for free, scale with low fees",
				viewDocs: "View Documentation",
			},
			footer: {
				agents: "Agents",
				apps: "Apps",
				blog: "Blog",
				brandAssets: "Brand Assets",
				changelog: "Changelog",
				chatPlayground: "Chat Playground",
				community: "Community",
				compare: "Compare",
				compareModels: "Compare Models",
				contactUs: "Contact Us",
				discounted: "Discounted",
				documentation: "Documentation",
				embeddings: "Embeddings",
				enterprise: "Enterprise",
				features: "Features",
				guides: "Guides",
				imageToImage: "Image to Image",
				integrations: "Integrations",
				mcpServer: "MCP Server",
				migrationGuides: "Migration Guides",
				models: "Models",
				privacy: "Privacy Policy",
				product: "Product",
				providers: "Providers",
				reasoning: "Reasoning",
				referralProgram: "Referral Program",
				resources: "Resources",
				rights: "All rights reserved.",
				socAlt: "AICPA SOC for Service Organizations badge",
				socLine1: "SOC 2 Type II",
				socLine2: "compliant",
				status: "All systems operational",
				templates: "Templates",
				terms: "Terms of Use",
				textGeneration: "Text Generation",
				textToImage: "Text to Image",
				tokenCostCalculator: "Token Cost Calculator",
				toolCalling: "Tool Calling",
				useCases: "Use Cases",
				videoGeneration: "Video Generation",
				vision: "Vision",
				webSearch: "Web Search",
			},
		},
		models: {
			search: {
				noResults: "No results found.",
				placeholder: "Search models...",
				providers: "Providers",
				trigger: "Search models by provider, name, ID, or alias...",
				unknownDate: "Unknown date",
			},
		},
		auth: {
			accountCreated: "Account created",
			accountCreatedDescription:
				"Please check your email to verify your account before signing in.",
			alreadyHaveAccount: "Already have an account? Sign in",
			backToSignIn: "Back to sign in",
			checkEmailPrefix: "If an account exists for",
			checkEmailSuffix:
				", we've sent a password reset link. The link expires in 1 hour.",
			checkEmailTitle: "Check your email",
			confirmPassword: "Confirm password",
			createAccount: "Create your free account",
			createAccountSubtitle:
				"No credit card required. Start building in seconds.",
			creatingAccount: "Creating account...",
			didNotGetIt: "Didn't get it? Check your spam folder, or",
			emailPlusNotAllowed: "Email addresses with '+' are not allowed",
			enterCredentials: "Enter your credentials to access your dashboard",
			failedPasskey: "Failed to sign in with passkey",
			failedResetPassword: "Failed to reset password",
			failedSendResetEmail: "Failed to send reset email",
			failedSignUp: "Failed to sign up",
			forgotPasswordTitle: "Forgot your password?",
			forgotPasswordSubtitle:
				"Enter your email and we'll send you a link to reset it.",
			hidePassword: "Hide password",
			loginSuccessful: "Login successful",
			minimumCharacters: "Minimum 8 characters",
			missingResetToken: "Missing reset token. Request a new reset link.",
			nameOptional: "Name (optional)",
			newPassword: "New password",
			noAccount: "Don't have an account? Sign up",
			or: "Or",
			passwordMismatch: "Passwords don't match",
			passwordMin: "Password must be at least 8 characters",
			passwordRequired: "Password is required",
			passwordUpdated: "Password updated",
			passwordUpdatedDescription: "Sign in with your new password.",
			requestNewLink: "Request a new link",
			resetInvalidDescription:
				"This password reset link is no longer valid. Request a new one to continue.",
			resetInvalidTitle: "Reset link invalid or expired",
			resetSubtitle: "Choose a strong password - at least 8 characters.",
			resetTitle: "Set a new password",
			sendResetLink: "Send reset link",
			sendingResetLink: "Sending reset link...",
			showPassword: "Show password",
			signingIn: "Signing in...",
			startFree: "Start free",
			subscribeProductUpdates: "Subscribe to product updates",
			tryAgain: "try again",
			updatePassword: "Update password",
			updatingPassword: "Updating password...",
			validEmail: "Please enter a valid email address",
		},
		onboarding: {
			apiKeyDescription: "Use this key to authenticate requests to doteb",
			apiKeyLoadFailed:
				"Could not load API key. You can create one from the dashboard.",
			apiKeyTitle: "Your API Key",
			dashboard: "Dashboard",
			defaultPrompt: "Explain what an LLM gateway is in 2 sentences.",
			documentation: "Documentation",
			goToDashboard: "Go to Dashboard",
			networkError: "Network error. Please try again.",
			noResponse: "No response received",
			playground: "Playground",
			promptPlaceholder: "Enter a prompt...",
			requestFailed: "Request failed",
			requestTimedOut: "Request timed out. Please try again.",
			response: "Response",
			sendRequest: "Send request",
			sending: "Sending...",
			settingUp: "Setting up...",
			streamingNotSupported: "Streaming not supported.",
			subtitle: "Your account is ready. Grab your API key and try it out.",
			title: "Make your first API call",
			tryDescription:
				"Send a real request using a free model - no credits needed",
			tryTitle: "Try it now",
			whatsNext: "What's next?",
		},
		quickStart: {
			copiedDescription: "Code snippet copied to clipboard",
			copiedTitle: "Copied to clipboard",
			copyCode: "Copy code",
			description:
				"Use your API key to make requests. doteb is compatible with the OpenAI SDK - just change the base URL - or use our dedicated AI SDK provider.",
			title: "Quick Start",
		},
		chatSupport: {
			closeChat: "Close chat",
			emailPlaceholder: "Your email (optional)",
			error: "Something went wrong. Please try again.",
			escalated:
				"We've notified our team. We'll reply here and via email shortly.",
			helpful: "Helpful",
			initialMessage:
				"Hi{name}! I'm the doteb support assistant. How can I help you today?",
			introduce: "Please introduce yourself before we start.",
			messagePlaceholder: "Ask about doteb...",
			namePlaceholder: "Your name *",
			newConversation: "Start a new conversation",
			notHelpful: "Not helpful",
			openChat: "Open chat support",
			rateConversation: "How would you rate this conversation?",
			resolve: "Resolve",
			resolved: "Conversation resolved. Thanks for your feedback!",
			resolveWithoutRating: "Resolve without rating",
			sendMessage: "Send message",
			starRating: "{count} star rating",
			startChat: "Start Chat",
			subtitle: "AI-powered help",
			supportTeam: "Support team",
			talkToHuman: "Talk to a human",
			title: "Support",
			welcome: "Welcome!",
		},
		billing: {
			addCredits: "Add credits",
			addCreditsAmount: "Add ${amount} credits ->",
			addPaymentMethod: "Add Payment Method",
			addPaymentMethodDescription:
				"Add a new card to your account for faster checkout.",
			amountInUsd: "Amount in USD",
			amountWholeDollar: "Amount must be a whole dollar amount.",
			autoReload: "Auto-reload",
			autoReloadEnabled: "Auto-reload enabled",
			autoReloadEnabledDescription:
				"Credits will reload ${amount} when your balance drops below ${threshold}. Manage in billing settings.",
			autoReloadPrompt: "Enable auto-reload?",
			autoReloadPromptDescription:
				"Automatically reload ${amount} when your balance drops below ${threshold}. You can turn it off anytime in billing settings.",
			autoTopUp: "Auto Top-Up",
			autoTopUpDescription:
				"Automatically add credits when your balance falls below a threshold",
			autoTopUpEnable: "Enable",
			autoTopUpEnableDescription:
				"Automatically charge your default payment method when credits are low",
			autoTopUpNoPaymentMethod:
				"You need to add a payment method before enabling auto top-up.",
			autoTopUpNoSelectedOrganization:
				"Please select an organization to manage auto top-up settings.",
			autoTopUpSavedDescription: "Your auto top-up settings have been updated.",
			autoTopUpSetDefault:
				"Please set a default payment method to enable auto top-up.",
			availableBalance: "Available Balance",
			back: "Back",
			billing: "Billing",
			billingDetails: "billing settings",
			billingPageDescription:
				"Manage your subscription plan and billing details",
			buyCredits: "Buy Credits",
			calculating: "Calculating...",
			calculatingFees: "Calculating fees...",
			cancel: "Cancel",
			cardAlreadyAdded: "Card already added",
			cardAlreadyAddedDescription:
				"This card is already linked to your account. Please use a different card.",
			cardDetails: "Card Details",
			checkOutWith: "or check out with",
			checkoutFailed: "Checkout Failed",
			checkoutFailedDescription: "Failed to create checkout session.",
			confirmPayment: "Confirm Payment",
			confirmPaymentDescription:
				"Review your payment details before confirming.",
			continue: "Continue",
			couldNotEnableAutoTopUp: "Could not enable auto top-up",
			couldNotEnableAutoTopUpDescription:
				"You can enable it later in billing settings.",
			credits: "Credits",
			creditsBalanceDescription:
				"Your current credit balance and top-up options",
			creditsHealthy: "Healthy balance",
			creditsHealthyDescription:
				"Your credit balance is sufficient for continued service.",
			creditsLoading: "Loading credits...",
			creditsLow: "Low credits",
			creditsLowDescription:
				"Your credit balance is running low. Consider topping up to avoid service interruption.",
			creditsRemaining:
				"~{days} days of credits remaining at your current usage",
			creditsRemainingOne: "~1 day of credits remaining at your current usage",
			creditsRemainingThirtyPlus: "30+ days of credits remaining",
			currentPlan: "Current Plan",
			default: "Default",
			deletePaymentMethodConfirm:
				"Are you sure you want to delete this payment method?",
			enableAutoReload: "Enable auto-reload",
			enabling: "Enabling...",
			error: "Error",
			errorSavingCard: "Error Saving Card",
			estimatedAutoTopUpFees: "Estimated Auto Top-up Fees",
			estimatedTotal: "Estimated total",
			expires: "Expires",
			internationalCardFee: "International card fee",
			internationalCardsFee:
				"International cards are subject to an additional 1.5% processing fee.",
			invoiceNote:
				"Need company/address details on your invoice? {link} before purchase. We email the invoice automatically after payment.",
			loadingPaymentForm: "Loading payment form...",
			loadingPaymentMethods: "Loading payment methods...",
			cardPaymentsUnavailable:
				"Card payments are unavailable in this deployment.",
			maxTopUpAmount: "Maximum top-up amount is ${amount}.",
			maximumAmount: "Maximum ${amount}",
			minTopUpAmount:
				"Minimum $10. Maximum ${amount}. Amount to add when auto top-up triggers.",
			minimumAmount: "Minimum ${amount}",
			noCredits: "No credits remaining",
			noCreditsDescription:
				"Your credit balance is empty. Top up now to continue using the service.",
			noPaymentMethods: "No payment methods added yet.",
			noRecentUsage: "No recent usage",
			orMore: "& more",
			payWithAlipay: "Pay with Alipay",
			payAmount: "Pay {amount}",
			paymentDetails: "Payment Details",
			paymentDetailsDescription:
				"Enter your card details to add ${amount} credits.",
			paymentFailed: "Payment Failed",
			paymentFailedDescription:
				"An error occurred while processing your payment.",
			paymentMethodAdded: "Payment method added successfully",
			paymentMethodDeleted: "Payment method deleted",
			paymentMethodUpdated: "Default payment method updated",
			paymentMethods: "Payment Methods",
			paymentMethodsDescription:
				"Manage your payment methods and billing information",
			paymentMethodsFailed: "Failed to load payment methods",
			paymentSummary: "Payment Summary",
			platformFee: "Platform fee (5%)",
			planAndBilling: "Plan & Billing",
			planBillingDescription: "Manage your billing preferences",
			planManagement: "Plan Management",
			processing: "Processing...",
			processingFee: "Processing",
			redirecting: "Redirecting...",
			saveCard: "Save this card for future payments",
			saveSettings: "Save Settings",
			saving: "Saving...",
			securedByStripe: "Secured by Stripe",
			selectPaymentMethod: "Select Payment Method",
			selectPaymentMethodDescription:
				"Choose a payment method to add ${amount} credits. Confirm details on the next step.",
			setDefault: "Set Default",
			settingsSaved: "Settings saved",
			success: "Success",
			threshold: "Threshold (USD)",
			thresholdHint: "Minimum $5. Top-up when credits fall below this amount.",
			topUpAmount: "Top-up Amount (USD)",
			topUpButton: "Top Up Credits",
			topUpCredits: "Top up credits",
			topUpDescription: "Add credits to your organization account.",
			topUpNow: "Top up now",
			total: "Total",
			totalInclProcessing: "Total (incl. processing)",
			transactionCreditGift: "Credit Gift",
			transactionCreditRefund: "Credit Refund",
			transactionCreditTopUp: "Credit Top-up",
			transactionDescription: "Description",
			transactionHistory: "Transaction History",
			transactionHistoryDescription:
				"View your organization's transaction history, including credit top-ups and subscription events.",
			transactionStatusCompleted: "completed",
			transactionStatusFailed: "failed",
			transactionStatusPending: "pending",
			transactionSubscriptionCancel: "Subscription Cancelled",
			transactionSubscriptionEnd: "Subscription Ended",
			transactionSubscriptionStart: "Subscription Start",
			transactions: "Transactions",
			transactionsEmpty: "No transactions found",
			validationAlreadyPurchased:
				"First-time credit bonus is only available for new customers.",
			validationEmailNotVerified:
				"Please verify your email to qualify for the first-time credit bonus.",
			validationPromotionUnavailable:
				"You are not eligible for the current promotion.",
			visaMastercardAmex: "Visa · Mastercard · Amex",
			youAreAllSet: "You're all set!",
			youAreAllSetDescription:
				"Your credits are ready. Start making API calls now.",
		},
		dashboard: {
			add: "Add",
			credits: "Credits",
			dark: "Dark",
			inviteAndEarn: "Invite & earn",
			inviteFriends: "Invite your friends",
			inviteFriendsDescription:
				"Invite friends and teammates and earn bonus credits",
			light: "Light",
			logOut: "Log out",
			project: "Project",
			selectTheme: "Select theme",
			system: "System",
			toolsResources: "Tools & Resources",
		},
		emailVerification: {
			error: "Error",
			failed: "Failed to send verification email",
			message:
				"Please check your inbox and click the verification link to access all features.",
			resend: "Resend Email",
			sending: "Sending...",
			sent: "Verification email sent",
			sentDescription: "Please check your inbox for the verification email.",
			title: "Your email is unverified.",
		},
		navigation: {
			account: "Account",
			activity: "Activity",
			agents: "Agents",
			apiKeys: "API Keys",
			auditLogs: "Audit Logs",
			billing: "Billing",
			chat: "Chat",
			dashboard: "Dashboard",
			documentation: "Documentation",
			guardrails: "Guardrails",
			masterKeys: "Master Keys",
			modelUsage: "Model Usage",
			organization: "Organization",
			policies: "Policies",
			preferences: "Preferences",
			providerKeys: "Provider Keys",
			referrals: "Referrals",
			routing: "Routing",
			sdk: "SDK",
			security: "Security",
			securityEvents: "Security Events",
			settings: "Settings",
			supportedModels: "Supported Models",
			team: "Team",
			transactions: "Transactions",
			usageMetrics: "Usage & Metrics",
			yourDiscounts: "Your Discounts",
		},
		apiKeys: {
			actions: "Actions",
			active: "Active",
			activeRule: "active rule",
			activeRules: "active rules",
			all: "All",
			allKeys: "All Keys",
			apiKey: "API Key",
			apiKeyCopied: "API Key Copied",
			apiKeyCopiedDescription: "The API key has been copied to your clipboard.",
			apiKeyCreated: "API Key Created",
			apiKeyCreatedDescription:
				"Your API key has been created. Please copy it now as you won't be able to see it again.",
			apiKeyCreatedIamHint:
				"You can now configure IAM rules for this key to control model access from the API Keys page.",
			apiKeyDeleted: "API key deleted successfully.",
			apiKeyLimitReached: "API key limit reached",
			apiKeyName: "API Key Name",
			apiKeyReactivated: "API Key Reactivated",
			apiKeyReactivatedDescription:
				"The API key is active again with a new expiration.",
			apiKeyRolled: "API Key Rolled",
			apiKeyRolledDescription: "A new secret has been generated for this key.",
			apiKeyStatusUpdated: "API Key Status Updated",
			apiKeyStatusUpdatedDescription: "The API key status has been updated.",
			apiKeysUsed: "API Keys: {current} of {max} used",
			activateKey: "Activate Key",
			allTimeUsageLimit: "All-time usage limit",
			copyApiKey: "Copy API key",
			createApiKey: "Create API Key",
			createApiKeyDescription: "Create a new API key to access doteb.",
			createApiKeyIamHint:
				"After creation, you can configure IAM rules to control access to specific models, providers, or pricing tiers.",
			createManageDescription:
				"Create and manage API keys to authenticate requests to doteb",
			created: "Created",
			createdBy: "Created By",
			currentPeriod: "Current Period",
			deactivateKey: "Deactivate Key",
			delete: "Delete",
			deleteConfirmDescription:
				"This action cannot be undone. This will permanently delete the API key and it will no longer be able to access your account.",
			deleteConfirmTitle: "Are you absolutely sure?",
			done: "Done",
			editLimitsDescription:
				"Update the all-time limit and the recurring usage window for this key.",
			editLimitsTitle: "Edit API key limits",
			every: "Every",
			expirationDescription:
				"The key is automatically disabled once it expires. You can reactivate it later by setting a new expiration.",
			expirationRange: "Expiration must be between 1 and {max} {unit}.",
			expired: "Expired",
			expires: "Expires",
			expiresIn: "Expires in",
			expiresOn: "Expires on {date}.",
			failedCreate: "Failed to create API key.",
			failedLoad: "Failed to load API keys. Please try again.",
			failedReactivate: "Failed to reactivate API key.",
			failedRoll: "Failed to roll API key.",
			failedUpdateLimits: "Failed to update API key limits.",
			iamRules: "IAM Rules",
			inactive: "Inactive",
			keyNameRequired: "Please enter an API key name.",
			limitReachedContact:
				"Limit reached - contact us at contact@doteb.com to unlock more",
			limitAllTimeInvalid:
				"All-time usage limit must be a non-negative number.",
			limitAllTimeRequired: "Enter an all-time usage limit or turn it off.",
			limitDurationRange: "Duration must be between 1 and {max} {unit}.",
			limitRecurringInvalid:
				"Recurring usage limit must be a non-negative number.",
			limitRecurringRequired: "Enter a recurring usage limit or turn it off.",
			limits: "Limits",
			limitsUpdated: "API Key Limits Updated",
			limitsUpdatedDescription: "The API key limits have been updated.",
			loading: "Loading API keys...",
			loadingProject: "Loading project information...",
			manageIamRules: "Manage IAM Rules",
			myKeys: "My Keys",
			name: "Name",
			noAllTimeLimit: "No all-time limit",
			noEmailAvailable: "No email available",
			noKeys: "No API keys have been created yet.",
			noProject: "Please select a project to view API keys.",
			noRecurringLimit: "No recurring limit",
			noRules: "No rules",
			noRulesConfigured: "No rules configured",
			minimumMaximumDuration: "Minimum 1 {minUnit}; maximum {max} {maxUnit}.",
			newApiKeyCopiedDescription:
				"The new API key has been copied to your clipboard.",
			placeholderName: "e.g. Production API Key",
			planAllowsMax: "{plan} plan allows maximum {max} API keys per project",
			project: "Project",
			reactivateDescriptionGeneric:
				"This key has expired. Set a new expiration to bring it back online.",
			reactivateDescriptionNamed:
				'"{name}" has expired. Set a new expiration to bring it back online.',
			reactivateKey: "Reactivate Key",
			reactivateTitle: "Reactivate API Key",
			reactivating: "Reactivating...",
			recurringResetDescription:
				"Current period usage resets when the configured window elapses.",
			recurringUsageLimit: "Recurring usage limit",
			rollKey: "Roll Key",
			rollDescriptionGeneric:
				"This generates a new secret for this key and immediately invalidates the current one. The key's stats, limits, IAM rules, and other settings are kept intact.",
			rollDescriptionNamed:
				'This generates a new secret for "{name}" and immediately invalidates the current one. The key\'s stats, limits, IAM rules, and other settings are kept intact.',
			rolling: "Rolling...",
			rollSuccessDescription:
				"A new secret has been generated. Please copy it now as you won't be able to see it again. All stats, limits, and settings for this key are preserved.",
			rollUpdateClients:
				"Make sure to update any clients using the old secret. The previous secret no longer works.",
			rule: "rule",
			rules: "rules",
			secureStore:
				"Make sure to store this API key securely. You won't be able to see it again.",
			setAllTimeLimit: "Set all-time usage limit",
			setExpirationTtl: "Set expiration (TTL)",
			setFutureExpiration: "Set a future expiration date.",
			setRecurringLimit: "Set recurring usage limit",
			status: "Status",
			ttlUnits: {
				day: { one: "day", other: "days" },
				hour: { one: "hour", other: "hours" },
				minute: { one: "minute", other: "minutes" },
			},
			title: "API Keys",
			unknown: "Unknown",
			unit: "Unit",
			units: {
				day: { one: "day", other: "days" },
				hour: { one: "hour", other: "hours" },
				month: { one: "month", other: "months" },
				week: { one: "week", other: "weeks" },
			},
			usage: "Usage",
			usageIncludesCreditsAndProviderKeys:
				"Usage includes both usage from doteb credits and usage from your own provider keys when applicable.",
			viewStatistics: "View Statistics",
		},
		providerKeys: {
			actions: "Actions",
			activate: "Activate",
			addKey: "Add Key",
			addNamedCompatibleProviders:
				"Add multiple named OpenAI-compatible providers.",
			addProviderKey: "Add Provider Key",
			addProviderKeyDescription:
				"Create a new provider key to connect to an LLM provider.",
			addProviderKeyFor: "Add {provider} Key",
			addProviderKeyForDescription:
				"Add an API key for {provider} to enable direct access.",
			baseUrl: "Base URL",
			baseUrlCustomRequired:
				"Base URL and custom name are required for custom provider",
			baseUrlRequired: "Base URL is required for doteb provider",
			buyCredits: "Buy Credits",
			creditsInstead: "Use credits instead",
			creditsRecommendation:
				"Skip the hassle of managing provider keys. Buy credits and get instant access to all models through a single API - with built-in fallbacks, load balancing, and usage analytics.",
			customName: "Custom Provider Name",
			customNameHint: "Used in model names like: {name}/gpt-4o",
			customNameInvalid: "Custom name must contain only lowercase letters a-z",
			deactivate: "Deactivate",
			delete: "Delete",
			deleteConfirmDescription:
				"This action cannot be undone. This will permanently delete the provider key and any applications using it will no longer be able to access the API.",
			deleteConfirmTitle: "Are you absolutely sure?",
			deleted: "Deleted",
			dismiss: "Dismiss",
			error: "Error",
			failedDelete: "Failed to delete key",
			failedStatus: "Failed to update status",
			keyCount: "{count} key",
			keyCountPlural: "{count} keys",
			keyRemoved: "Provider key removed",
			learnMore: "Learn more",
			loadBalanceDescription:
				"Add one or more keys to load balance requests for this provider.",
			noKeys: "No keys added yet.",
			noOrganization: "Please select an organization to view provider keys.",
			noProvidersAvailable: "No providers available",
			loadingProviders: "Loading providers...",
			optionApiVersion: "API Version",
			optionCrossRegionPrefix: "Cross-Region Prefix",
			optionDeployment: "Deployment",
			optionGoogleProjectId: "Google Cloud Project ID",
			optionRegion: "Region",
			optionResource: "Resource",
			optionValidationModel: "Validation Model",
			organization: "Organization",
			provider: "Provider",
			providerKeyCreated: "Provider Key Created",
			providerKeyCreatedDescription:
				"The provider key has been validated and saved.",
			providerKeysDescription:
				"Provider keys allow you to use your own API keys with doteb without additional fees.",
			providerRequired: "Please select a provider",
			recommended: "Recommended",
			resourceNameRequired: "Azure resource name is required",
			selectProvider: "Select provider...",
			status: {
				active: "active",
				inactive: "inactive",
			},
			statusUpdated: "Status Updated",
			statusUpdatedDescription: "Provider key marked as {status}",
			title: "Provider Keys",
			token: "Provider API Key",
			tokenRequired: "Please enter the provider API key",
			validationFailed: "Validation Failed",
			validationFailedDescription:
				"Failed to validate the API key. Please check your key and region.",
			validating: "Validating...",
			validatingApiKey: "Validating API Key",
			validatingApiKeyDescription: "Please wait...",
		},
	},
	"zh-CN": {
		landing: {
			nav: {
				ai: {
					agents: {
						title: "智能体",
						description: "预置带工具调用能力的 AI 智能体。",
					},
					agentSkills: {
						title: "智能体技能",
						description: "适用于 Claude Code 和其他 AI 智能体的技能。",
					},
					aiSdk: {
						description: "在 Vercel AI SDK 中使用 doteb。",
					},
					guides: {
						title: "指南",
						description: "覆盖各类框架的集成和使用指南。",
					},
					mcp: {
						description: "通过 MCP 协议把 AI 助手连接到 200+ LLM。",
					},
					templates: {
						title: "模板",
						description: "面向 AI 应用的生产可用模板。",
					},
				},
				closeMenu: "关闭菜单",
				dashboard: "控制台",
				docs: "文档",
				getStarted: "开始使用",
				home: "首页",
				login: "登录",
				models: "模型",
				openMenu: "打开菜单",
				pricing: "价格",
				productsLabel: "产品",
				resourcesLabel: "资源",
				products: {
					aiGateway: {
						title: "AI 网关",
						description: "通过一个统一 API 端点路由到 200+ LLM。",
					},
					chatPlayground: {
						title: "聊天调试台",
						description: "即时测试提示词，并并排比较模型响应。",
					},
					devPass: {
						description:
							"面向 Claude Code、Cursor 和各类编码工具的固定月费方案。",
					},
					observability: {
						title: "可观测性",
						description: "用实时分析面板监控用量、成本和延迟。",
					},
				},
				resources: {
					apps: {
						title: "应用",
						description: "浏览可配合 doteb 使用的应用和工具。",
					},
					blog: {
						title: "博客",
						description: "产品更新、教程、基准测试和公告。",
					},
					changelog: {
						title: "更新日志",
						description: "了解 doteb 的最新发布内容。",
					},
					compare: {
						title: "对比",
						description: "并排比较模型。",
					},
					enterprise: {
						title: "企业版",
						description: "为团队提供自定义账单、更长留存和优先支持。",
					},
					guardrails: {
						title: "护栏",
						description: "通过内容审核和安全过滤保护你的 AI。",
					},
					integrations: {
						title: "集成",
						description: "无缝连接常用框架、SDK 和工具。",
					},
					models: {
						title: "模型",
						description: "浏览所有可用 LLM 模型和能力。",
					},
					modelTimeline: {
						title: "模型时间线",
						description: "跟踪所有模型的发布历史。",
					},
					providers: {
						title: "供应商",
						description: "连接并管理你的供应商 API 密钥。",
					},
					referralProgram: {
						title: "推荐计划",
						description: "赚取 LLM 消费额的 1%。",
					},
					reliability: {
						title: "可靠性",
						description: "跨供应商自动故障转移，达到 99.9999% 有效可用性。",
					},
					tokenCostCalculator: {
						title: "Token 成本计算器",
						description: "即时计算 LLM token 成本和节省金额。",
					},
				},
			},
			hero: {
				announcement: "doteb 已通过 SOC 2 Type II 认证",
				dashboardAlt: "展示分析和 API 用量的 doteb 控制台",
				docsCta: "查看文档",
				primaryCta: "获取我的 API 密钥",
				subtitle:
					"不用再来回管理 API 密钥和供应商控制台。通过 400+ 模型路由请求，实时跟踪成本，并在不改代码的情况下切换供应商。",
				switching: "正在从其他供应商迁移？",
				title: "doteb - 一个 API 连接 DeepSeek、通义千问、Kimi 和 40+ 供应商",
				trust: {
					freeTier: "包含免费额度",
					noCard: "无需信用卡",
					setup: "30 秒完成设置",
				},
				viewAll: "查看全部",
				viewAllProviders: "查看所有供应商",
			},
			features: {
				eyebrow: "平台能力",
				learnMore: "了解更多",
				titleLine1: "放心交付所需的",
				titleLine2: "完整能力",
				items: {
					"unified-api-interface": {
						title: "统一 API 接口",
						description:
							"保留现有 OpenAI SDK 代码，只需修改 base URL 即可上线。",
					},
					"multi-provider-support": {
						title: "多供应商支持",
						description:
							"一个集成访问 25+ 供应商，避免厂商锁定，随时切换模型。",
					},
					"performance-monitoring": {
						title: "性能监控",
						description:
							"比较不同模型的延迟、成本和质量，为每个场景选择最合适方案。",
					},
					"secure-key-management": {
						title: "安全密钥管理",
						description:
							"一个控制台管理所有供应商密钥，不再散落凭据或暴露密钥。",
					},
					"self-hosted-or-cloud": {
						title: "自托管或云服务",
						description:
							"可在自己的基础设施上运行，也可以交给我们托管，由你选择。",
					},
					"cost-aware-analytics": {
						title: "成本感知分析",
						description:
							"查看 7 天或 30 天内的请求、token、总花费和每 1K token 平均成本。",
					},
					"per-model-provider-breakdown": {
						title: "按模型和供应商拆解",
						description: "按供应商和模型拆解用量与花费，快速定位昂贵异常项。",
					},
					"errors-reliability-monitoring": {
						title: "错误与可靠性监控",
						description: "直接在控制台监控错误率、缓存命中率和可靠性趋势。",
					},
					"project-level-usage-explorer": {
						title: "项目级用量分析",
						description:
							"用专属图表和表格深入查看每个项目的请求、模型、错误、缓存和成本。",
					},
					"audit-logs": {
						title: "企业审计日志",
						description: "跟踪谁在何时做了什么，并通过完整审计轨迹保持合规。",
					},
					guardrails: {
						title: "LLM 护栏",
						description: "防止提示词注入、检测 PII，并用智能护栏拦截恶意请求。",
					},
				},
			},
			graph: {
				description:
					"你的应用只发送一个请求。我们会路由到 DeepSeek、通义千问、Kimi 或任意 40+ 供应商，并自动选择最佳路径。",
				eyebrow: "工作原理",
				requestModel: "请求新增模型",
				stats: {
					models: "模型",
					providers: "供应商",
					tokens: "已路由 token",
				},
				title: "一个请求，任意模型。",
				tooltipLink: "联系我们",
				tooltipPrefix: "想接入你的模型？",
				viewAllModels: "查看所有模型",
			},
			codeExample: {
				bullets: {
					baseUrl: "只改一行 base URL",
					sdks: "兼容 OpenAI、Anthropic 和 Vercel AI SDK",
					tracking: "每个请求都会记录成本、延迟和 token 用量",
				},
				copiedButton: "已复制",
				copiedDescription: "{language} 代码片段已复制到剪贴板。",
				copiedTitle: "已复制到剪贴板",
				copyButton: "复制",
				copyFailedDescription: "无法复制到剪贴板，请重试。",
				copyFailedTitle: "复制失败",
				description:
					"已经在用 OpenAI SDK？只改一行 base URL 即可完成。适用于任何语言或框架。",
				eyebrow: "集成",
				titleLine1: "即插即用兼容。",
				titleLine2: "零学习成本。",
			},
			testimonials: {
				eyebrow: "社区",
				title: "受到全球开发者信任",
			},
			faq: {
				contactLink: "联系我们",
				contactPrefix: "没有找到答案？",
				description: "关于价格、模型和上手使用，你需要知道的都在这里。",
				eyebrow: "常见问题",
				title: "常见问题",
				items: {
					openRouter: {
						answer:
							"不同于 OpenRouter，doteb 支持完整自托管、更深入的实时分析、免费 BYOK，以及灵活的企业增强能力。",
						intro: "不同于 OpenRouter，我们提供：",
						point1: "通过部署包完整自托管，可运行在你的基础设施上。",
						point2: "针对每个请求的更深入实时成本和延迟分析。",
						point3: "BYOK：免费使用你自己的供应商 API 密钥。",
						point4: "灵活的企业增强能力，包括专属分片和自定义 SLA。",
						question: "doteb 和 OpenRouter 有什么不同？",
						schemaAnswer:
							"不同于 OpenRouter，doteb 支持完整自托管、更深入的实时成本和延迟分析、免费 BYOK，以及灵活的企业增强能力。",
					},
					models: {
						answerPrefix:
							"我们支持跨 40+ 供应商的 400+ 模型，包括 GPT-4o、Claude、Gemini、Llama、Mistral 等。查看",
						answerSuffix: "获取完整列表。新模型通常会在发布后 48 小时内加入。",
						modelsPage: "模型页面",
						question: "支持哪些模型？",
						schemaAnswer:
							"我们支持跨 40+ 供应商的 400+ 模型，包括 GPT-4o、Claude、Gemini、Llama、Mistral 等，并通常在新模型发布后 48 小时内加入。",
					},
					uptime: {
						answer:
							"公开状态页会展示实时指标。企业实例包含 99.9% 可用性 SLA；自托管安装取决于你的基础设施。",
						question: "可用性保障是什么？",
						schemaAnswer:
							"公开状态页会展示实时指标。企业实例包含 99.9% 可用性 SLA；自托管安装取决于你的基础设施。",
					},
					pricing: {
						byok: "BYOK 免费：使用自己的 LLM 供应商 API 密钥并直接向供应商付费，同时包含用量跟踪和分析。",
						credits: "额度模式收取 5% 平台费：即用即付购买额度并调用任意模型。",
						enterprise:
							"企业版：自定义 SLA、专属基础设施、自带云容量和批量折扣。",
						intro: "我们的价格简单透明：",
						question: "费用是多少？",
						schemaAnswer:
							"额度模式按量付费并收取 5% 平台费；BYOK 可免费使用自己的供应商 API 密钥；企业版包含自定义 SLA 和专属基础设施；可为专属部署提供自托管。",
						selfHost:
							"自托管：使用受支持的部署包，在自己的基础设施上部署网关。",
					},
				},
			},
			enterprise: {
				description:
					"当 LLM 基础设施成为关键系统，你需要专属支持、合规控制，以及匹配业务目标的基础设施。",
				eyebrow: "企业版",
				explore: "了解企业版",
				items: {
					"Enterprise SSO": {
						title: "企业 SSO",
						description: "基于 SAML 和 OIDC 的单点登录，并支持角色权限控制",
					},
					"Self-hosted or Managed": {
						title: "自托管或托管",
						description: "部署在你的基础设施上，或交给我们托管并提供 99.9% SLA",
					},
					"Volume Pricing": {
						title: "批量价格",
						description: "随用量扩展的自定义限流和价格方案",
					},
					"White-label Ready": {
						title: "支持白标",
						description: "用你的品牌提供白标网关和聊天调试台",
					},
				},
				talkToSales: "联系销售",
				titleLine1: "为规模化交付的",
				titleLine2: "团队而构建",
				trust: {
					onboarding: "上线协助",
					slas: "自定义 SLA",
					soc2: "SOC 2 进行中",
					support: "优先支持",
				},
			},
			cta: {
				createAccount: "创建免费账号",
				description:
					"加入成千上万开发者，通过 doteb 处理 100B+ token。包含免费额度，无需信用卡。",
				selfHost: "自托管 doteb",
				titleLine1: "30 秒内",
				titleLine2: "开始路由请求",
			},
			newsletter: {
				description:
					"订阅后每周获取 LLM 路由、新模型发布和成本优化洞察，直接发送到你的邮箱。",
				error: "出了点问题，请重试。",
				eyebrow: "订阅更新",
				noSpam: "不发送垃圾邮件，可随时退订。",
				perks: {
					beta: "抢先体验测试版功能",
					models: "第一时间了解新模型和供应商",
					tips: "降低延迟和成本的实践建议",
				},
				subscribe: "免费订阅",
				subscribing: "订阅中...",
				successDescription: "请查看收件箱，我们只发送有价值的内容。",
				successTitle: "订阅成功！",
				title: "保持技术领先",
			},
			pricingPlans: {
				badge: "价格",
				contactSales: "联系销售",
				custom: "定制",
				description: "免费计划包含完整功能，没有隐藏费用。",
				footerContact: "联系销售团队",
				footerLine1: "所有计划都包含 API、文档和社区支持。",
				footerLine2: "需要定制方案？",
				forever: "永久",
				free: "免费",
				getStarted: "开始使用",
				goDashboard: "进入控制台",
				plans: {
					selfHost: {
						description: "部署在你自己的基础设施上",
						features: {
							community: "社区支持",
							control: "完全掌控你的数据",
							freeForever: "永久免费",
							infrastructure: "运行在你的基础设施上",
							noLimits: "无用量限制",
							updates: "持续更新",
						},
						name: "自托管",
					},
					free: {
						description: "面向所有人的完整功能计划",
						features: {
							analytics: "高级分析",
							byok: "自带密钥免费使用",
							credits: "使用额度付费（5% 服务费）",
							models: "访问全部模型",
							retention: "30 天数据留存",
							routing: "自动路由和供应商选择",
							support: "邮件支持",
							team: "团队管理",
						},
						name: "免费",
					},
					enterprise: {
						description: "适合有定制需求的大型组织",
						features: {
							discounts: "批量折扣",
							everything: "包含免费版全部能力",
							onboarding: "上手协助",
							priority: "优先功能请求",
							retention: "无限数据留存",
							seats: "不限席位",
							sso: "单点登录（SSO）",
							support: "7x24 高级支持",
							whiteLabel: "聊天应用（含白标）",
						},
						name: "企业版",
					},
				},
				recommended: "推荐",
				title: "免费开始，以低费率扩展",
				viewDocs: "查看文档",
			},
			footer: {
				agents: "智能体",
				apps: "应用",
				blog: "博客",
				brandAssets: "品牌资产",
				changelog: "更新日志",
				chatPlayground: "聊天调试台",
				community: "社区",
				compare: "对比",
				compareModels: "对比模型",
				contactUs: "联系我们",
				discounted: "折扣模型",
				documentation: "文档",
				embeddings: "向量嵌入",
				enterprise: "企业版",
				features: "功能",
				guides: "指南",
				imageToImage: "图生图",
				integrations: "集成",
				mcpServer: "MCP 服务",
				migrationGuides: "迁移指南",
				models: "模型",
				privacy: "隐私政策",
				product: "产品",
				providers: "供应商",
				reasoning: "推理",
				referralProgram: "推荐计划",
				resources: "资源",
				rights: "保留所有权利。",
				socAlt: "AICPA 服务组织 SOC 徽章",
				socLine1: "SOC 2 Type II",
				socLine2: "合规",
				status: "所有系统运行正常",
				templates: "模板",
				terms: "使用条款",
				textGeneration: "文本生成",
				textToImage: "文生图",
				tokenCostCalculator: "Token 成本计算器",
				toolCalling: "工具调用",
				useCases: "使用场景",
				videoGeneration: "视频生成",
				vision: "视觉",
				webSearch: "网页搜索",
			},
		},
		models: {
			search: {
				noResults: "未找到结果。",
				placeholder: "搜索模型...",
				providers: "供应商",
				trigger: "按供应商、名称、ID 或别名搜索模型...",
				unknownDate: "未知日期",
			},
		},
		auth: {
			accountCreated: "账号已创建",
			accountCreatedDescription: "请先查收邮件并验证账号，然后再登录。",
			alreadyHaveAccount: "已有账号？登录",
			backToSignIn: "返回登录",
			checkEmailPrefix: "如果账号存在：",
			checkEmailSuffix: "，我们已发送密码重置链接。链接将在 1 小时后失效。",
			checkEmailTitle: "请查收邮件",
			confirmPassword: "确认密码",
			createAccount: "创建免费账号",
			createAccountSubtitle: "无需信用卡，几秒钟即可开始构建。",
			creatingAccount: "正在创建账号...",
			didNotGetIt: "没有收到？请检查垃圾邮件，或",
			emailPlusNotAllowed: "不允许使用包含 + 的邮箱地址",
			enterCredentials: "输入凭据以访问控制台",
			failedPasskey: "通行密钥登录失败",
			failedResetPassword: "重置密码失败",
			failedSendResetEmail: "发送重置邮件失败",
			failedSignUp: "注册失败",
			forgotPasswordTitle: "忘记密码？",
			forgotPasswordSubtitle: "输入邮箱，我们会发送重置链接。",
			hidePassword: "隐藏密码",
			loginSuccessful: "登录成功",
			minimumCharacters: "至少 8 个字符",
			missingResetToken: "缺少重置令牌。请重新请求重置链接。",
			nameOptional: "姓名（可选）",
			newPassword: "新密码",
			noAccount: "还没有账号？注册",
			or: "或",
			passwordMismatch: "两次输入的密码不一致",
			passwordMin: "密码至少需要 8 个字符",
			passwordRequired: "请输入密码",
			passwordUpdated: "密码已更新",
			passwordUpdatedDescription: "请使用新密码登录。",
			requestNewLink: "重新请求链接",
			resetInvalidDescription: "该密码重置链接已失效。请重新请求链接后继续。",
			resetInvalidTitle: "重置链接无效或已过期",
			resetSubtitle: "请选择强密码，至少 8 个字符。",
			resetTitle: "设置新密码",
			sendResetLink: "发送重置链接",
			sendingResetLink: "正在发送重置链接...",
			showPassword: "显示密码",
			signingIn: "正在登录...",
			startFree: "免费开始",
			subscribeProductUpdates: "订阅产品更新",
			tryAgain: "重试",
			updatePassword: "更新密码",
			updatingPassword: "正在更新密码...",
			validEmail: "请输入有效的邮箱地址",
		},
		onboarding: {
			apiKeyDescription: "使用此密钥向 doteb 发起认证请求。",
			apiKeyLoadFailed: "无法加载 API 密钥。你可以在控制台中创建一个。",
			apiKeyTitle: "你的 API 密钥",
			dashboard: "控制台",
			defaultPrompt: "用两句话解释什么是 LLM 网关。",
			documentation: "文档",
			goToDashboard: "进入控制台",
			networkError: "网络错误，请重试。",
			noResponse: "未收到响应",
			playground: "调试台",
			promptPlaceholder: "输入提示词...",
			requestFailed: "请求失败",
			requestTimedOut: "请求超时，请重试。",
			response: "响应",
			sendRequest: "发送请求",
			sending: "正在发送...",
			settingUp: "正在设置...",
			streamingNotSupported: "当前环境不支持流式响应。",
			subtitle: "账号已就绪。获取 API 密钥并试用一次请求。",
			title: "发起你的第一次 API 调用",
			tryDescription: "使用免费模型发送真实请求，无需额度。",
			tryTitle: "立即试用",
			whatsNext: "下一步",
		},
		quickStart: {
			copiedDescription: "代码片段已复制到剪贴板",
			copiedTitle: "已复制到剪贴板",
			copyCode: "复制代码",
			description:
				"使用 API 密钥发起请求。doteb 兼容 OpenAI SDK，只需修改 base URL，也可以使用专用 AI SDK provider。",
			title: "快速开始",
		},
		chatSupport: {
			closeChat: "关闭聊天",
			emailPlaceholder: "你的邮箱（可选）",
			error: "出现错误，请重试。",
			escalated: "我们已通知支持团队。稍后会在这里和邮件中回复你。",
			helpful: "有帮助",
			initialMessage: "你好{name}！我是 doteb 支持助手。今天有什么可以帮你？",
			introduce: "开始前请先介绍一下你自己。",
			messagePlaceholder: "询问 doteb...",
			namePlaceholder: "你的姓名 *",
			newConversation: "开始新对话",
			notHelpful: "没有帮助",
			openChat: "打开聊天支持",
			rateConversation: "你如何评价这次对话？",
			resolve: "解决",
			resolved: "对话已解决。感谢你的反馈！",
			resolveWithoutRating: "不评分并解决",
			sendMessage: "发送消息",
			starRating: "{count} 星评分",
			startChat: "开始聊天",
			subtitle: "AI 辅助帮助",
			supportTeam: "支持团队",
			talkToHuman: "联系人工",
			title: "支持",
			welcome: "欢迎！",
		},
		billing: {
			addCredits: "添加额度",
			addCreditsAmount: "添加 ${amount} 额度 ->",
			addPaymentMethod: "添加支付方式",
			addPaymentMethodDescription: "添加新银行卡，后续结账更快。",
			amountInUsd: "金额（美元）",
			amountWholeDollar: "金额必须为整数美元。",
			autoReload: "自动充值",
			autoReloadEnabled: "自动充值已启用",
			autoReloadEnabledDescription:
				"余额低于 ${threshold} 时会自动充值 ${amount}。可在账单设置中管理。",
			autoReloadPrompt: "启用自动充值？",
			autoReloadPromptDescription:
				"余额低于 ${threshold} 时自动充值 ${amount}。你可以随时在账单设置中关闭。",
			autoTopUp: "自动充值",
			autoTopUpDescription: "当余额低于阈值时自动添加额度",
			autoTopUpEnable: "启用",
			autoTopUpEnableDescription: "额度不足时自动从默认支付方式扣款",
			autoTopUpNoPaymentMethod: "启用自动充值前需要先添加支付方式。",
			autoTopUpNoSelectedOrganization: "请选择组织以管理自动充值设置。",
			autoTopUpSavedDescription: "自动充值设置已更新。",
			autoTopUpSetDefault: "请先设置默认支付方式再启用自动充值。",
			availableBalance: "可用余额",
			back: "返回",
			billing: "账单",
			billingDetails: "账单设置",
			billingPageDescription: "管理订阅计划和账单详情",
			buyCredits: "购买额度",
			calculating: "正在计算...",
			calculatingFees: "正在计算费用...",
			cancel: "取消",
			cardAlreadyAdded: "该银行卡已添加",
			cardAlreadyAddedDescription: "该银行卡已绑定到账号。请使用其他银行卡。",
			cardDetails: "银行卡信息",
			checkOutWith: "或使用以下方式结账",
			checkoutFailed: "结账失败",
			checkoutFailedDescription: "创建结账会话失败。",
			confirmPayment: "确认付款",
			confirmPaymentDescription: "确认前请检查付款详情。",
			continue: "继续",
			couldNotEnableAutoTopUp: "无法启用自动充值",
			couldNotEnableAutoTopUpDescription: "你可以稍后在账单设置中启用。",
			credits: "额度",
			creditsBalanceDescription: "当前额度余额和充值选项",
			creditsHealthy: "余额充足",
			creditsHealthyDescription: "当前额度余额足够继续使用服务。",
			creditsLoading: "正在加载额度...",
			creditsLow: "额度偏低",
			creditsLowDescription: "额度余额偏低。建议充值以避免服务中断。",
			creditsRemaining: "按当前用量约剩余 {days} 天额度",
			creditsRemainingOne: "按当前用量约剩余 1 天额度",
			creditsRemainingThirtyPlus: "剩余 30 天以上额度",
			currentPlan: "当前计划",
			default: "默认",
			deletePaymentMethodConfirm: "确定要删除这个支付方式吗？",
			enableAutoReload: "启用自动充值",
			enabling: "正在启用...",
			error: "错误",
			errorSavingCard: "保存银行卡出错",
			estimatedAutoTopUpFees: "预计自动充值费用",
			estimatedTotal: "预计合计",
			expires: "过期时间",
			internationalCardFee: "国际银行卡费用",
			internationalCardsFee: "国际银行卡会额外收取 1.5% 处理费。",
			invoiceNote:
				"发票需要公司/地址信息？请在购买前{link}。付款后我们会自动通过邮件发送发票。",
			loadingPaymentForm: "正在加载支付表单...",
			loadingPaymentMethods: "正在加载支付方式...",
			cardPaymentsUnavailable: "当前部署未启用银行卡支付。",
			maxTopUpAmount: "最大充值金额为 ${amount}。",
			maximumAmount: "最高 ${amount}",
			minTopUpAmount: "最低 $10，最高 ${amount}。自动充值触发时将添加该金额。",
			minimumAmount: "最低 ${amount}",
			noCredits: "额度已用尽",
			noCreditsDescription: "额度余额为空。请立即充值以继续使用服务。",
			noPaymentMethods: "尚未添加支付方式。",
			noRecentUsage: "近期无用量",
			orMore: "及更多",
			payWithAlipay: "使用支付宝支付",
			payAmount: "支付 {amount}",
			paymentDetails: "付款详情",
			paymentDetailsDescription: "输入银行卡信息以添加 ${amount} 额度。",
			paymentFailed: "付款失败",
			paymentFailedDescription: "处理付款时发生错误。",
			paymentMethodAdded: "支付方式添加成功",
			paymentMethodDeleted: "支付方式已删除",
			paymentMethodUpdated: "默认支付方式已更新",
			paymentMethods: "支付方式",
			paymentMethodsDescription: "管理支付方式和账单信息",
			paymentMethodsFailed: "支付方式加载失败",
			paymentSummary: "付款摘要",
			platformFee: "平台费用（5%）",
			planAndBilling: "计划与账单",
			planBillingDescription: "管理账单偏好",
			planManagement: "计划管理",
			processing: "处理中...",
			processingFee: "处理费",
			redirecting: "正在跳转...",
			saveCard: "保存此银行卡用于后续付款",
			saveSettings: "保存设置",
			saving: "正在保存...",
			securedByStripe: "由 Stripe 安全处理",
			selectPaymentMethod: "选择支付方式",
			selectPaymentMethodDescription:
				"选择一个支付方式以添加 ${amount} 额度，下一步确认详情。",
			setDefault: "设为默认",
			settingsSaved: "设置已保存",
			success: "成功",
			threshold: "阈值（美元）",
			thresholdHint: "最低 $5。额度低于该金额时触发充值。",
			topUpAmount: "充值金额（美元）",
			topUpButton: "充值额度",
			topUpCredits: "充值额度",
			topUpDescription: "向组织账号添加额度。",
			topUpNow: "立即充值",
			total: "合计",
			totalInclProcessing: "合计（含处理费）",
			transactionCreditGift: "额度赠送",
			transactionCreditRefund: "额度退款",
			transactionCreditTopUp: "额度充值",
			transactionDescription: "描述",
			transactionHistory: "交易记录",
			transactionHistoryDescription:
				"查看组织交易记录，包括额度充值和订阅事件。",
			transactionStatusCompleted: "已完成",
			transactionStatusFailed: "失败",
			transactionStatusPending: "待处理",
			transactionSubscriptionCancel: "订阅已取消",
			transactionSubscriptionEnd: "订阅已结束",
			transactionSubscriptionStart: "订阅开始",
			transactions: "交易记录",
			transactionsEmpty: "没有交易记录",
			validationAlreadyPurchased: "首充奖励仅面向新客户。",
			validationEmailNotVerified: "请先验证邮箱以获得首充额度奖励。",
			validationPromotionUnavailable: "你不符合当前促销资格。",
			visaMastercardAmex: "Visa · Mastercard · Amex",
			youAreAllSet: "已准备就绪！",
			youAreAllSetDescription: "额度已到账。现在可以开始调用 API。",
		},
		dashboard: {
			add: "添加",
			credits: "额度",
			dark: "深色",
			inviteAndEarn: "邀请并赚取",
			inviteFriends: "邀请好友",
			inviteFriendsDescription: "邀请好友和团队成员，赚取奖励额度",
			light: "浅色",
			logOut: "退出登录",
			project: "项目",
			selectTheme: "选择主题",
			system: "跟随系统",
			toolsResources: "工具与资源",
		},
		emailVerification: {
			error: "错误",
			failed: "发送验证邮件失败",
			message: "请查收邮件并点击验证链接，以访问全部功能。",
			resend: "重新发送邮件",
			sending: "正在发送...",
			sent: "验证邮件已发送",
			sentDescription: "请查收收件箱中的验证邮件。",
			title: "你的邮箱尚未验证。",
		},
		navigation: {
			account: "账号",
			activity: "调用记录",
			agents: "Agent",
			apiKeys: "API 密钥",
			auditLogs: "审计日志",
			billing: "账单",
			chat: "聊天",
			dashboard: "控制台",
			documentation: "文档",
			guardrails: "护栏",
			masterKeys: "主密钥",
			modelUsage: "模型用量",
			organization: "组织",
			policies: "策略",
			preferences: "偏好设置",
			providerKeys: "供应商密钥",
			referrals: "推荐奖励",
			routing: "路由",
			sdk: "SDK",
			security: "安全",
			securityEvents: "安全事件",
			settings: "设置",
			supportedModels: "支持的模型",
			team: "团队",
			transactions: "交易记录",
			usageMetrics: "用量与指标",
			yourDiscounts: "你的折扣",
		},
		apiKeys: {
			actions: "操作",
			active: "启用",
			activeRule: "条启用规则",
			activeRules: "条启用规则",
			all: "全部",
			allKeys: "全部密钥",
			apiKey: "API 密钥",
			apiKeyCopied: "API 密钥已复制",
			apiKeyCopiedDescription: "API 密钥已复制到剪贴板。",
			apiKeyCreated: "API 密钥已创建",
			apiKeyCreatedDescription:
				"API 密钥已创建。请立即复制，之后将无法再次查看。",
			apiKeyCreatedIamHint:
				"现在可以在 API Keys 页面为此密钥配置 IAM 规则，控制模型访问。",
			apiKeyDeleted: "API 密钥已删除。",
			apiKeyLimitReached: "已达到 API 密钥数量上限",
			apiKeyName: "API 密钥名称",
			apiKeyReactivated: "API 密钥已重新启用",
			apiKeyReactivatedDescription:
				"API 密钥已重新启用，并设置了新的过期时间。",
			apiKeyRolled: "API 密钥已轮换",
			apiKeyRolledDescription: "已为该密钥生成新的 secret。",
			apiKeyStatusUpdated: "API 密钥状态已更新",
			apiKeyStatusUpdatedDescription: "API 密钥状态已更新。",
			apiKeysUsed: "API 密钥：已使用 {current} / {max}",
			activateKey: "启用密钥",
			allTimeUsageLimit: "总用量限制",
			copyApiKey: "复制 API 密钥",
			createApiKey: "创建 API 密钥",
			createApiKeyDescription: "创建新的 API 密钥以访问 doteb。",
			createApiKeyIamHint:
				"创建后可配置 IAM 规则，控制特定模型、供应商或价格层级的访问。",
			createManageDescription: "创建和管理 API 密钥，用于认证 doteb 请求",
			created: "创建时间",
			createdBy: "创建者",
			currentPeriod: "当前周期",
			deactivateKey: "停用密钥",
			delete: "删除",
			deleteConfirmDescription:
				"此操作无法撤销。API 密钥将被永久删除，并且无法再访问你的账号。",
			deleteConfirmTitle: "确定要继续吗？",
			done: "完成",
			editLimitsDescription: "更新此密钥的总量限制和周期用量窗口。",
			editLimitsTitle: "编辑 API 密钥限制",
			every: "每",
			expirationDescription:
				"密钥过期后会自动停用。之后可以通过设置新的过期时间重新启用。",
			expirationRange: "过期时间必须介于 1 到 {max} {unit} 之间。",
			expired: "已过期",
			expires: "过期于",
			expiresIn: "过期倒计时",
			expiresOn: "将于 {date} 过期。",
			failedCreate: "创建 API 密钥失败。",
			failedLoad: "API 密钥加载失败，请重试。",
			failedReactivate: "重新启用 API 密钥失败。",
			failedRoll: "轮换 API 密钥失败。",
			failedUpdateLimits: "更新 API 密钥限制失败。",
			iamRules: "IAM 规则",
			inactive: "停用",
			keyNameRequired: "请输入 API 密钥名称。",
			limitReachedContact: "已达到上限 - 请联系 contact@doteb.com 解锁更多",
			limitAllTimeInvalid: "总用量限制必须是非负数字。",
			limitAllTimeRequired: "请输入总用量限制，或关闭此项。",
			limitDurationRange: "周期时长必须介于 1 到 {max} {unit} 之间。",
			limitRecurringInvalid: "周期用量限制必须是非负数字。",
			limitRecurringRequired: "请输入周期用量限制，或关闭此项。",
			limits: "限制",
			limitsUpdated: "API 密钥限制已更新",
			limitsUpdatedDescription: "API 密钥限制已更新。",
			loading: "正在加载 API 密钥...",
			loadingProject: "正在加载项目信息...",
			manageIamRules: "管理 IAM 规则",
			myKeys: "我的密钥",
			name: "名称",
			noAllTimeLimit: "无总量限制",
			noEmailAvailable: "无邮箱信息",
			noKeys: "尚未创建 API 密钥。",
			noProject: "请选择项目以查看 API 密钥。",
			noRecurringLimit: "无周期限制",
			noRules: "无规则",
			noRulesConfigured: "未配置规则",
			minimumMaximumDuration: "最小 1 {minUnit}；最大 {max} {maxUnit}。",
			newApiKeyCopiedDescription: "新的 API 密钥已复制到剪贴板。",
			placeholderName: "例如：生产环境 API Key",
			planAllowsMax: "{plan} 计划每个项目最多允许 {max} 个 API 密钥",
			project: "项目",
			reactivateDescriptionGeneric:
				"此密钥已过期。请设置新的过期时间以重新启用。",
			reactivateDescriptionNamed:
				"“{name}” 已过期。请设置新的过期时间以重新启用。",
			reactivateKey: "重新启用密钥",
			reactivateTitle: "重新启用 API 密钥",
			reactivating: "正在重新启用...",
			recurringResetDescription: "当前周期用量会在配置的窗口结束后重置。",
			recurringUsageLimit: "周期用量限制",
			rollKey: "轮换密钥",
			rollDescriptionGeneric:
				"这会为此密钥生成新的 secret，并立即使当前 secret 失效。该密钥的统计、限制、IAM 规则和其他设置会保留。",
			rollDescriptionNamed:
				"这会为“{name}”生成新的 secret，并立即使当前 secret 失效。该密钥的统计、限制、IAM 规则和其他设置会保留。",
			rolling: "正在轮换...",
			rollSuccessDescription:
				"新的 secret 已生成。请立即复制，之后将无法再次查看。此密钥的统计、限制和设置都会保留。",
			rollUpdateClients:
				"请确保更新所有仍在使用旧 secret 的客户端。旧 secret 已无法继续使用。",
			rule: "条规则",
			rules: "条规则",
			secureStore: "请安全保存此 API 密钥。之后将无法再次查看。",
			setAllTimeLimit: "设置总用量限制",
			setExpirationTtl: "设置过期时间 (TTL)",
			setFutureExpiration: "请设置未来的过期时间。",
			setRecurringLimit: "设置周期用量限制",
			status: "状态",
			ttlUnits: {
				day: { one: "天", other: "天" },
				hour: { one: "小时", other: "小时" },
				minute: { one: "分钟", other: "分钟" },
			},
			title: "API 密钥",
			unknown: "未知",
			unit: "单位",
			units: {
				day: { one: "天", other: "天" },
				hour: { one: "小时", other: "小时" },
				month: { one: "个月", other: "个月" },
				week: { one: "周", other: "周" },
			},
			usage: "用量",
			usageIncludesCreditsAndProviderKeys:
				"用量包含 doteb 额度消耗，以及适用时你自己的供应商密钥消耗。",
			viewStatistics: "查看统计",
		},
		providerKeys: {
			actions: "操作",
			activate: "启用",
			addKey: "添加密钥",
			addNamedCompatibleProviders: "添加多个命名的 OpenAI 兼容供应商。",
			addProviderKey: "添加供应商密钥",
			addProviderKeyDescription: "创建新的供应商密钥以连接 LLM 供应商。",
			addProviderKeyFor: "添加 {provider} 密钥",
			addProviderKeyForDescription:
				"添加 {provider} 的 API 密钥以启用直连访问。",
			baseUrl: "Base URL",
			baseUrlCustomRequired: "自定义供应商需要填写 Base URL 和自定义名称",
			baseUrlRequired: "doteb 供应商需要填写 Base URL",
			buyCredits: "购买额度",
			creditsInstead: "改用额度",
			creditsRecommendation:
				"跳过管理供应商密钥的繁琐步骤。购买额度即可通过单个 API 立即访问全部模型，并内置故障切换、负载均衡和用量分析。",
			customName: "自定义供应商名称",
			customNameHint: "用于模型名称，例如：{name}/gpt-4o",
			customNameInvalid: "自定义名称只能包含小写字母 a-z",
			deactivate: "停用",
			delete: "删除",
			deleteConfirmDescription:
				"此操作无法撤销。供应商密钥将被永久删除，使用它的应用将无法再访问 API。",
			deleteConfirmTitle: "确定要继续吗？",
			deleted: "已删除",
			dismiss: "关闭",
			error: "错误",
			failedDelete: "删除密钥失败",
			failedStatus: "更新状态失败",
			keyCount: "{count} 个密钥",
			keyCountPlural: "{count} 个密钥",
			keyRemoved: "供应商密钥已移除",
			learnMore: "了解更多",
			loadBalanceDescription:
				"添加一个或多个密钥，为该供应商的请求做负载均衡。",
			noKeys: "尚未添加密钥。",
			noOrganization: "请选择组织以查看供应商密钥。",
			noProvidersAvailable: "暂无可用供应商",
			loadingProviders: "正在加载供应商...",
			optionApiVersion: "API 版本",
			optionCrossRegionPrefix: "跨区域前缀",
			optionDeployment: "部署",
			optionGoogleProjectId: "Google Cloud 项目 ID",
			optionRegion: "区域",
			optionResource: "资源",
			optionValidationModel: "验证模型",
			organization: "组织",
			provider: "供应商",
			providerKeyCreated: "供应商密钥已创建",
			providerKeyCreatedDescription: "供应商密钥已验证并保存。",
			providerKeysDescription:
				"供应商密钥允许你在 doteb 中使用自己的 API 密钥，且不产生额外费用。",
			providerRequired: "请选择供应商",
			recommended: "推荐",
			resourceNameRequired: "请填写 Azure 资源名称",
			selectProvider: "选择供应商...",
			status: {
				active: "启用",
				inactive: "停用",
			},
			statusUpdated: "状态已更新",
			statusUpdatedDescription: "供应商密钥已标记为 {status}",
			title: "供应商密钥",
			token: "供应商 API 密钥",
			tokenRequired: "请输入供应商 API 密钥",
			validationFailed: "验证失败",
			validationFailedDescription: "API 密钥验证失败。请检查密钥和区域。",
			validating: "正在验证...",
			validatingApiKey: "正在验证 API 密钥",
			validatingApiKeyDescription: "请稍候...",
		},
	},
} satisfies Partial<Record<Locale, TranslationTree>>;

export const uiMessages = mergeUiMessages(
	baseUiMessages,
	authPageMessages,
	publicPageMessages,
	publicModelPageMessages,
	publicDirectoryPageMessages,
	dashboardPageMessages,
	dashboardOrgPageMessages,
);
